/* ==========================================================================
   EDS · Notify me
   Custom element behind blocks/eds-pdp-notify.liquid. No dependencies.

   A port of the `#oosNotifyForm` script that lives inline in
   sections/main-product.liquid (around line 1130) onto the eds variant
   contract. That file is untouched; this is the parallel implementation the
   eds product templates use.

   THREE THINGS, exactly as the original did them:

     1. Reveal. The panel is shown while the selected variant is unavailable and
        hidden while it is buyable. `available === false` is the only test, which
        is the test the original made.

     2. Re-tag. On every variant change the form's data-variant-id,
        data-variant-sku and data-variant-title are rewritten from the picker's
        JSON island, so a submit always reports the variant on screen.

     3. Send. On submit, identify the shopper to Cordial and fire
        `oos_notify_signup` with the same payload keys the original sent.

   PLUS ONE THING THE ORIGINAL DID NOT DO: the same submit also creates a
   Klaviyo back in stock subscription (client API, public company ID) against
   the selected variant, so Klaviyo can automate restock alerts.

   INDEPENDENT SENDS. Cordial and Klaviyo are separate calls and neither can
   affect the other: a missing or throwing `crdl` does not stop Klaviyo, and a
   Klaviyo failure — which is asynchronous and never awaited — does not stop
   Cordial or change what the shopper sees. The thank-you shows if at least one
   went out. Only when neither did is the submit abandoned with nothing on
   screen moving, so the shopper never gets a thank-you for a sign-up that went
   nowhere.

   CORDIAL IS NOT IN THIS THEME. Nothing here defines `crdl`; it arrives from
   outside — a script tag or a tag manager — so it is present on production and
   typically absent from a dev store. A dev store that logs the Cordial warning
   is therefore not evidence of a bug, and Klaviyo still sends there.

   A back in stock subscription is NOT marketing consent. Opting the shopper in
   to marketing email is a separate call, not made here.

   ONCE SENT, DONE. The original hid the form and left it hidden for the rest of
   the page view, even if the shopper moved to a different sold-out variant. So
   does this: hiding the form is what latches, so a later variant change
   re-reveals the panel with the thank-you in it, not a second empty form.

   Markup contract (see the block for the authoritative version):
     <eds-notify [hidden] [data-eds-notify-pinned]>
       <form data-eds-notify-form data-product-id data-variant-id
             data-variant-sku data-product-title data-variant-title
             data-klaviyo-company-id data-klaviyo-catalog-prefix>
         <input name="email">
       <p data-eds-notify-success hidden>

   data-eds-notify-pinned is set by the block in the theme editor only; it keeps
   the panel visible so a merchant can see and select the block on a product
   that happens to be in stock.

   No animation, so nothing here needs a prefers-reduced-motion branch.
   ========================================================================== */

(() => {
  if (customElements.get('eds-notify')) return;

  /* Klaviyo versions its API by date and supports each revision for two years.
     2026-10-15 was current when this was written; bump it before October 2028.
     The company ID and catalog prefix are block settings, not constants here —
     see blocks/eds-pdp-notify.liquid. */
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const KLAVIYO_REVISION = '2026-10-15';

  /* DEBUG LOGGING, off for shoppers. `?oos_debug=1` on any URL turns it on and
     remembers that in this browser's localStorage, so it survives moving
     between products; `?oos_debug=0` turns it off again. Storage can be
     blocked (private windows, strict settings), so every access is guarded and
     the URL flag still works for that one page view without it. */
  const DEBUG_KEY = 'eds-oos-debug';
  const DEBUG = (() => {
    let flag = null;
    try {
      flag = new URLSearchParams(window.location.search).get('oos_debug');
    } catch (error) {}

    try {
      if (flag === '1') window.localStorage.setItem(DEBUG_KEY, '1');
      if (flag === '0') window.localStorage.removeItem(DEBUG_KEY);
    } catch (error) {}

    if (flag === '1') return true;
    if (flag === '0') return false;
    try {
      return window.localStorage.getItem(DEBUG_KEY) === '1';
    } catch (error) {
      return false;
    }
  })();

  const debug = (...args) => {
    if (DEBUG) console.log('%c[OOS debug]', 'color:#0a7;font-weight:bold', ...args);
  };

  if (DEBUG) debug('Debug logging is on. Add ?oos_debug=0 to the URL to turn it off.');

  class EdsNotify extends HTMLElement {
    // Arrow-function fields, not methods: these need stable identities so
    // removeEventListener can find them again on disconnect.

    /* Same reading as assets/eds-buy-buttons.js. The eds picker sends
       `variant: null` for a combination that matches no real variant — a
       meaningful state — so an absent `variant` KEY and a present-but-null one
       have to be told apart. Other emitters pass the variant as the detail. */
    #onVariantChange = (event) => {
      const detail = event.detail;
      if (!detail) return;

      const hasKey = Object.prototype.hasOwnProperty.call(detail, 'variant');
      const variant = hasKey ? detail.variant : detail;

      // Anything that is neither null nor a real variant is not a signal — an
      // empty detail must not read as "sold out" and raise the form.
      if (variant && variant.id == null) return;

      this.update(variant);
    };

    #onFormChange = (event) => {
      const target = event.target;
      if (!target || target.name !== 'id') return;
      this.update(this.#variantFromId(target.value));
    };

    /* Cordial and Klaviyo are sent side by side and know nothing of each
       other — see INDEPENDENT SENDS above. The thank-you shows if at least one
       of them went out; if neither did, nothing on screen moves. */
    #onSubmit = (event) => {
      event.preventDefault();

      const email = this.email ? this.email.value.trim() : '';
      if (!email) {
        debug('Submit ignored: email field is empty.');
        return;
      }

      const data = this.form.dataset;
      debug('Submit.', { email: email, form: { ...data } });

      const sentCordial = this.#sendCordial(email, data);
      const sentKlaviyo = this.#sendKlaviyo(email, data);
      debug('Dispatched.', { cordial: sentCordial, klaviyo: sentKlaviyo });

      if (!sentCordial && !sentKlaviyo) {
        debug('Neither was sent, so the form stays as it is.');
        return;
      }

      this.form.hidden = true;
      if (this.success) this.success.hidden = false;
      debug('Thank-you shown. The Klaviyo response is logged when it arrives.');
    };

    connectedCallback() {
      this.form = this.querySelector('[data-eds-notify-form]');
      this.success = this.querySelector('[data-eds-notify-success]');
      this.email = this.form ? this.form.querySelector('input[name="email"]') : null;

      if (!this.form) {
        debug('No [data-eds-notify-form] inside <eds-notify>; nothing wired.');
        return;
      }

      debug('Connected.', {
        visible: !this.hidden,
        pinned: this.hasAttribute('data-eds-notify-pinned'),
        form: { ...this.form.dataset },
        cordialLoaded: typeof crdl === 'function',
      });

      this.form.addEventListener('submit', this.#onSubmit);

      // The eds variant picker is not the only thing that can move the variant,
      // so accept the generic event and a raw form change too.
      document.addEventListener('eds:variant:change', this.#onVariantChange);
      document.addEventListener('variant:change', this.#onVariantChange);
      document.addEventListener('change', this.#onFormChange);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.#onSubmit);

      document.removeEventListener('eds:variant:change', this.#onVariantChange);
      document.removeEventListener('variant:change', this.#onVariantChange);
      document.removeEventListener('change', this.#onFormChange);
    }

    /* -- Send ------------------------------------------------------------- */

    /* Returns whether the sign-up was handed to Cordial.

       THE BARE IDENTIFIER, NOT `window.crdl`, which is what the original used
       and is the safer of the two. A global declared with top-level `let` or
       `const` is script-scoped and never becomes a property of window, so
       `window.crdl` would read undefined while `crdl` resolves perfectly well.
       Cordial is injected outside the theme — a script tag or a tag manager —
       so its snippet is not ours to check. `typeof` on an undeclared identifier
       is legal and does not throw.

       The try/catch is there so a Cordial snippet that throws cannot take the
       Klaviyo send down with it. */
    #sendCordial(email, data) {
      if (typeof crdl !== 'function') {
        console.warn('[OOS] Cordial listener missing.');
        return false;
      }

      try {
        const payload = {
          email: email,
          product_id: data.productId,
          variant_id: data.variantId,
          variant_sku: data.variantSku,
          product_title: data.productTitle,
          variant_title: data.variantTitle,
          timestamp: new Date().toISOString(),
        };

        crdl('contact', { email: email });
        debug('Cordial: crdl("contact") called.', { email: email });
        crdl('event', 'oos_notify_signup', payload);
        debug('Cordial: crdl("event", "oos_notify_signup") called.', payload);
        return true;
      } catch (error) {
        console.error('[OOS] Cordial sign-up failed.', error);
        return false;
      }
    }

    /* Returns whether the request was dispatched — not whether Klaviyo
       accepted it. The fetch is fire-and-forget: nothing waits on it, so a slow
       or failing Klaviyo never delays the thank-you or touches Cordial.

       `fetch` only rejects on a network failure; a 4xx from Klaviyo resolves
       normally, so the status is checked by hand and the error body logged —
       otherwise a rejected subscription would be invisible. Success is 202. */
    #sendKlaviyo(email, data) {
      const companyId = (data.klaviyoCompanyId || '').trim();
      const catalogPrefix = data.klaviyoCatalogPrefix || '';

      // A blank company ID is the off switch, set from the block settings.
      if (!companyId) {
        debug('Klaviyo: skipped, company ID is blank in the block settings.');
        return false;
      }

      if (!data.variantId) {
        console.warn('[OOS] Klaviyo skipped: no variant id on the form.');
        return false;
      }

      try {
        const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`;
        const body = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: { type: 'profile', attributes: { email: email } },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `${catalogPrefix}${data.variantId}`,
                },
              },
            },
          },
        };
        const started = Date.now();

        debug('Klaviyo: POST', url, { revision: KLAVIYO_REVISION, body: body });

        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(body),
        })
          .then((response) => {
            const ms = Date.now() - started;
            if (response.ok) {
              debug(`Klaviyo: accepted (${response.status}) in ${ms}ms.`);
              return;
            }
            return response.text().then((text) => {
              console.error(`[OOS] Klaviyo rejected the subscription (${response.status}).`, text);
              debug(`Klaviyo: rejected (${response.status}) in ${ms}ms.`, text);
            });
          })
          .catch((error) => {
            console.error('[OOS] Klaviyo back in stock subscription failed.', error);
            debug('Klaviyo: network failure. CORS, an ad blocker or being offline are the usual causes.', error);
          });
        return true;
      } catch (error) {
        console.error('[OOS] Klaviyo back in stock subscription failed.', error);
        return false;
      }
    }

    /* -- Update ------------------------------------------------------------ */

    /* A null variant is an unavailable OPTION COMBINATION, not a sold-out
       product. There is nothing to notify anyone about — no variant id to send —
       so the panel stays down, and the form keeps the attributes it had rather
       than being blanked. */
    update(variant) {
      if (!variant) return;

      this.#retag(variant);

      if (this.hasAttribute('data-eds-notify-pinned')) return;
      this.hidden = variant.available !== false;
      debug(`Variant ${variant.id}: ${this.hidden ? 'available, panel hidden' : 'sold out, panel shown'}.`);
    }

    /* `name || title` reproduces the original's quirk — see the block's doc. */
    #retag(variant) {
      this.form.setAttribute('data-variant-id', variant.id);
      this.form.setAttribute('data-variant-sku', variant.sku || '');
      this.form.setAttribute('data-variant-title', variant.name || variant.title || '');
    }

    /* A bare form change carries an id, not a variant. The picker's JSON island
       is the only place on the page with the availability behind that id, so
       read it from there rather than guessing the variant is buyable. */
    #variantFromId(id) {
      const island = document.querySelector('[data-eds-variant-data]');
      if (!island) return null;
      try {
        const variants = JSON.parse(island.textContent);
        if (!Array.isArray(variants)) return null;
        return variants.find((v) => String(v.id) === String(id)) || null;
      } catch (error) {
        return null;
      }
    }
  }

  customElements.define('eds-notify', EdsNotify);
})();
